export interface AiChat {
  id: string;
  title?: string;
  preview?: string;
  updatedAt?: number;
  /**
   * SD-2661: epoch millis of when the chat was pinned, or `null` when it is not pinned. Decides
   * "Pinned" vs "Recents" membership and orders the pinned section (most recent first).
   *
   * Always present (never `undefined`) on a normalized chat: the BE omits the field entirely when
   * a chat is unpinned, and the reducer merges chats by spreading, so a missing key would leave a
   * stale pin in place after an unpin.
   */
  pinnedAt?: number | null;
  /**
   * SD-2661: set when the chat has been soft-deleted server-side. Soft-deleted chats are filtered
   * out of queries, so this only ever arrives on the live WS broadcast — the BE persists the
   * delete as an UPDATE, so subscribers see `op:'upd'` carrying this rather than `op:'del'`.
   */
  deletedAt?: number | null;
}
export type ChatEventSrc = 'user' | 'bot';

export interface ToolExecutionRecord {
  toolName: string;
  label: string;
  durationMs: number;
  failed: boolean;
}

export type ChatStatusType =
  | 'tool_started'
  | 'tool_completed'
  | 'tool_failed'
  | 'pending_approval'
  | 'action_card';

interface ChatStatusBase {
  chatId: string;
}

export interface ChatStatusToolPayload extends ChatStatusBase {
  status: 'tool_started' | 'tool_completed' | 'tool_failed';
  toolName: string;
  label: string;
}

/**
 * SD-2326: fires once when the Langgraph proposeAction node interrupts. The BE
 * has registered a pending-approval row keyed by {@link pendingApprovalId} and
 * is waiting for the user's decision via POST `/api/v1/allo/approve-action`.
 * {@link cardJson} is a JSON-stringified {@code DynamicActionCardPayload}.
 */
export interface ChatStatusPendingApprovalPayload extends ChatStatusBase {
  status: 'pending_approval';
  pendingApprovalId: string;
  cardJson: string;
}

/**
 * SD-2798 / SD-2865: fires when a tool returns an action card, at the moment the
 * tool completes and before the model has written any of its reply. The card no
 * longer depends on the model echoing the tool's JSON: this frame renders it
 * live, and the same card arrives on the persisted event's `actionCards`.
 * {@link cardJson} is the tool's raw `{type, editable, action, card}` envelope.
 */
export interface ChatStatusActionCardPayload extends ChatStatusBase {
  status: 'action_card';
  cardJson: string;
}

export type ChatStatusPayload =
  | ChatStatusToolPayload
  | ChatStatusPendingApprovalPayload
  | ChatStatusActionCardPayload;

export function isPendingApprovalStatus(
  status: ChatStatusPayload
): status is ChatStatusPendingApprovalPayload {
  return status.status === 'pending_approval';
}

export function isActionCardStatus(
  status: ChatStatusPayload
): status is ChatStatusActionCardPayload {
  return status.status === 'action_card';
}

/**
 * SD-2155 / SD-2153: wire-only payload pushed by the BE while Allo is
 * generating a response. The FE assembles the in-flight assistant bubble
 * from `delta` chunks; the canonical persisted {@link AiChatEvent} still
 * arrives at the end with the same `ts` so the bubble can reconcile.
 *
 * - `start`    — fires once before any tokens; FE creates an in-flight bubble
 *                keyed by `chatId+ts`.
 * - `delta`    — N batched chunks (~150ms apart). BE has already buffered any
 *                `<action-card>` blocks so `delta` text never splits a card.
 * - `complete` — fires once when the LLM finishes; the final `chatEvent` (op
 *                `ins`, ent `chatEvent`) follows shortly with the full text.
 * - `error`    — SD-2450: terminal failure marker sent *instead* of `complete`
 *                when the turn fails (e.g. a mid-stream connection reset). The
 *                FE flips the in-flight bubble to an interrupted state (keeping
 *                whatever partial text arrived) and offers a retry. No
 *                `chatEvent` follows. `code` distinguishes a retryable transport
 *                drop (`interrupted`) from any other failure (`failed`).
 */
// keepAlive: non-terminal liveness ping — the generation went quiet mid-turn
// without failing, so the FE can show a "still working…" status instead of
// a frozen bubble. Cleared by the next delta/complete/error.
// resyncMiss: SD-2526 Stage B — reply to a `resync` request the BE could not serve
// (turn already evicted / unknown); the FE relies on its parallel getChatEvents reload.
export type ChatStreamType =
  | 'start'
  | 'delta'
  | 'complete'
  | 'error'
  | 'keepAlive'
  | 'resyncMiss';

/**
 * SD-2450: failure class carried on a `type === 'error'` chatStream payload.
 *
 * SD-2765 adds `cancelled`, which is not a failure — the user stopped the turn, or it hit the
 * server's wall-clock cap. It rides the same terminal signal because the bubble treatment is the
 * same (keep the partial, stop the spinner, offer retry); only the copy differs.
 */
export type ChatStreamErrorCode = 'interrupted' | 'failed' | 'cancelled';

export interface ChatStreamPayload {
  chatId: string;
  ts: number;
  type: ChatStreamType;
  /** Present only when `type === 'delta'`. */
  delta?: string;
  /** Monotonic per-stream counter starting at 0. Lets the FE detect drops. */
  seq?: number;
  /** Present only when `type === 'error'` (SD-2450). */
  code?: ChatStreamErrorCode;
}

export interface AiChatEvent {
  chatId: string;
  ts: number;
  src: ChatEventSrc;
  data: string;
  documentIds?: string[];
  toolsUsed?: ToolExecutionRecord[];
  /**
   * SD-2798: action cards the turn's tools returned, as their raw
   * `{type, editable, action, card}` envelopes, captured by the BE when each tool
   * returned rather than parsed out of the reply text. Absent on events written
   * before SD-2798, which carry their cards inside `data` as `<action-card>` blocks.
   */
  actionCards?: string[];
  /**
   * SD-2765: the turn was stopped before it finished. Absent on everything else. The live stream
   * signal only reaches whoever was watching at the time, so this is what makes a reloaded chat
   * still show the response as stopped rather than as a reply that ends mid-sentence.
   */
  cancelled?: boolean;
}

/**
 * SD-2096: the caller's Allo cross-session memory state, as returned by
 * `GET`/`DELETE /api/v2/allo/preferences` and `PUT /api/v2/allo/preferences/opt-out`.
 *
 * `summary` is the bullet-list of preferences Allo inferred from prior chats — `null` until
 * something has been inferred. `memoryOptOut` true means the user turned the feature off, which
 * stops both extraction and injection but leaves any existing summary stored.
 */
export interface AlloPreferences {
  summary: string | null;
  memoryOptOut: boolean;
  updatedAt: string | null;
}
